<script setup>
  import { computed } from "vue";
  import { useRoute } from "vue-router";

  defineProps({
    navigate: {
      type: Function,
      default: () => {},
    },
  });

  const route = useRoute();
  const isTestimony = computed(() => route.path.includes("/testimonies"));

  const journey = [
    {
      year: "2019",
      title: "The Beginning",
      description:
        "Swif Study Abroad was founded with a small, dedicated team and a big vision.",
    },
    {
      year: "2020",
      title: "First 100 Students",
      description:
        "We proudly sent our first 100 students to their dream destinations abroad.",
    },
    {
      year: "2021",
      title: "University Partnerships",
      description:
        "Established key partnerships with top universities across Europe and North America.",
    },
    {
      year: "2023",
      title: "1000+ Students Strong",
      description:
        "Celebrated the milestone of helping over a thousand students achieve their study abroad goals.",
    },
  ];

  const universityLogos = [
    { name: "CIMT", src: require("../assets/images/cimt.jpeg") },
    { name: "University of Manitoba", src:require("../assets/images/manitoba.jpeg") },
    { name: "University of Calgary", src: require("../assets/images/Ucalagary.png") },
    { name: "George Brown College", src:  require("../assets/images/george_brown.jpeg") },
    { name: "Mohawk College", src: require("../assets/images/mohawk.png") },
    { name: "York University", src: require("../assets/images/york.png")  },
  ];
</script>

<template>
  <div>
  <router-view v-if="isTestimony" :navigate="navigate" />
  <div v-else class="bg-white text-gray-800">
    <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
      <section class="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
        <div class="text-left" v-reveal:right>
          <h1 class="text-5xl md:text-6xl font-serif text-red-700 leading-tight">
            About Swif Study Abroad
          </h1>
          <h2 class="text-4xl md:text-5xl font-serif text-gray-900 mt-2">
            Our Mission & Vision
          </h2>
          <div class="mt-6 text-gray-600 space-y-4">
            <p>
              Our mission is to empower students to become global citizens by
              providing them with unparalleled opportunities for international
              education. We envision a world where every student has the chance
              to broaden their horizons and gain a deeper understanding of
              different cultures.
            </p>
            <p>
              Since our inception, Swif Study Abroad has been driven by a
              passion for connecting students with transformative educational
              experiences. We believe that studying abroad is not just about
              academics; it's about personal growth, cultural immersion, and
              building a global network.
            </p>
          </div>
        </div>
        <div v-reveal:left>
          <img
            class="rounded-lg shadow-lg"
            src="../assets/images/canadian_besties.jpg"
            alt="College students living abroad."
          />
        </div>
      </section>

      <section class="py-20 bg-gray-50">
        <h2 class="text-4xl md:text-5xl font-serif text-center text-gray-900 mb-16">
          Our Guiding Principles
        </h2>
        <div class="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8 px-4">
          <!-- Integrity Card -->
          <div class="bg-white p-8 rounded-xl shadow-lg text-center hover:-translate-y-1 transition duration-300" v-reveal:up="0">
            <div class="text-5xl text-yellow-600 mx-auto mb-4">
              <!-- Shield Check Icon -->
              <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 4.04A12.001 12.001 0 002.944 12c0 3.072 1.14 5.966 3.17 8.192a12.001 12.001 0 0017.884-4.04A11.955 11.955 0 0121.056 12c0-3.072-1.14-5.966-3.17-8.192z" />
              </svg>
            </div>
            <h3 class="text-xl font-semibold text-gray-900 mb-2">Integrity</h3>
            <p class="text-gray-600 text-sm">
              We operate with transparency and honesty in all our interactions.
            </p>
          </div>

          <!-- Excellence Card -->
          <div class="bg-white p-8 rounded-xl shadow-lg text-center hover:-translate-y-1 transition duration-300" v-reveal:up="100">
            <div class="text-5xl text-yellow-600 mx-auto mb-4">
              <!-- Medal Icon -->
              <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.519 4.674c.3.921-.755 1.688-1.539 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.784.57-1.838-.197-1.539-1.118l1.519-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.382-1.81.588-1.81h4.915a1 1 0 00.95-.69l1.519-4.674z" />
              </svg>
            </div>
            <h3 class="text-xl font-semibold text-gray-900 mb-2">Excellence</h3>
            <p class="text-gray-600 text-sm">
              We strive for the highest standards in our services and support.
            </p>
          </div>

          <!-- Student-Centric Card -->
          <div class="bg-white p-8 rounded-xl shadow-lg text-center hover:-translate-y-1 transition duration-300" v-reveal:up="200">
            <div class="text-5xl text-yellow-600 mx-auto mb-4">
              <!-- Users Icon -->
              <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h3 class="text-xl font-semibold text-gray-900 mb-2">Student-Centric</h3>
            <p class="text-gray-600 text-sm">
              Our students' success and well-being are at the core of everything we do.
            </p>
          </div>
        </div>
      </section>

      <!-- journey section -->
      <section class="py-20">
        <h2 class="text-4xl md:text-5xl font-serif text-center text-gray-900">
          Our Journey
        </h2>
        <div class="mt-12 relative">
          <div class="absolute left-1/2 transform -translate-x-1/2 h-full border-l-2 border-gray-200"></div>
          <div class="space-y-16">
            <div 
              v-for="(item, index) in journey" 
              :key="index" 
              class="relative flex items-center"
              :class="{ 'flex-row-reverse': index % 2 !== 0 }"
              v-reveal:up="index * 120"
            >
              <div class="w-1/2 px-8">
                <div 
                  class="absolute top-1/2 transform -translate-y-1/2 bg-yellow-400 w-4 h-4 rounded-full"
                  :class="index % 2 === 0 ? 'right-1/2 -mr-2' : 'left-1/2 -ml-2'"
                ></div>
                <div :class="index % 2 === 0 ? 'text-right' : 'text-left'">
                  <h3 class="text-2xl font-semibold text-red-700">
                    {{ item.year }} - {{ item.title }}
                  </h3>            
                  <p class="mt-2 text-gray-600">{{ item.description }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Testimonies -->
      <section class="py-20 bg-gray-50">
        <div class="max-w-3xl mx-auto text-center px-4" v-reveal:up>
          <h2 class="text-4xl md:text-5xl font-serif text-gray-900">
            Stories That Speak for Us
          </h2>
          <p class="mt-6 text-gray-600">
            The best way to understand who we are is to hear from the students we've
            helped. Read real, unfiltered journeys of visas approved, dreams realized,
            and lives changed abroad.
          </p>
          <router-link
            to="/about/testimonies"
            class="mt-8 inline-block bg-red-700 text-white font-semibold px-8 py-3 rounded-lg shadow-lg hover:bg-red-800 transition duration-300"
          >
            Read Student Testimonies
          </router-link>
        </div>
      </section>

      <!-- Partnering Universities -->
          <section class="py-20">
        <h2 class="text-4xl md:text-5xl font-serif text-center text-gray-900 mb-16">
          Our University Partners
        </h2>
        <div class="max-w-6xl mx-auto grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-8 items-center justify-items-center px-4">
          <div v-for="(logo, index) in universityLogos" :key="logo.name" v-reveal:up="index * 60" class="p-4 flex justify-center items-center h-20 w-full">
            <img
              :src="logo.src"
              :alt="logo.name + ' Logo'"
              class="max-w-full h-auto object-contain filter grayscale hover:grayscale-0 transition-all duration-300"
            />
          </div>
        </div>
      </section>
    </main>
  </div>
  </div>
</template>

<style scoped>
/* Scoped styles can be added here if necessary */
</style>

   
